import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

interface PricingRoute {
  route: string;
  economico: number;
  conforto: number;
  suv: number;
  mpv: number;
  minivan: number;
  minibus: number;
  luxo: number;
}

// Função para verificar se estamos no período de inverno (novembro a março)
const isWinterSeason = () => {
  const currentMonth = new Date().getMonth(); // 0 = Janeiro, 10 = Novembro
  return currentMonth >= 10 || currentMonth <= 2; // Novembro (10) até Março (2)
};

// Função para verificar se a rota é para Espanha
const isSpainRoute = (route: string) => {
  return route.includes('(Espanha)') || route.includes('Gibraltar');
};

// Função para aplicar desconto de inverno
const applyWinterDiscount = (price: number, route: string) => {
  if (isWinterSeason()) {
    // 10% de desconto para rotas em Espanha, 30% para rotas em Portugal
    return isSpainRoute(route) ? price * 0.9 : price * 0.7;
  }
  return price;
};

export default function BookingEngine() {
  const { t, i18n } = useTranslation();
  
  // Inicializar Supabase
  const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
  );
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    from: '',
    to: '',
    date: '',
    time: '',
    passengers: '1',
    vehicleType: 'economico',
    returnTrip: false,
    returnDate: '',
    returnTime: '',
    returnFrom: '',
    returnTo: '',
    differentReturnLocation: false,
    childSeat0to1: 0,
    childSeat1to3: 0,
    childSeat5to12: 0,
    acceptPrivacy: false,
    observations: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [reservationNumber, setReservationNumber] = useState<number | null>(null);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [matchedRoute, setMatchedRoute] = useState<PricingRoute | null>(null);
  const [fromSuggestions, setFromSuggestions] = useState<string[]>([]);
  const [toSuggestions, setToSuggestions] = useState<string[]>([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);

  const winterSeason = isWinterSeason();

  // Definir capacidade de passageiros por tipo de veículo
  const vehicleCapacity: { [key: string]: number } = {
    economico: 3,
    conforto: 3,
    suv: 4,
    mpv: 5,
    minivan: 6,
    minibus: 8,
    luxo: 3
  };

  // Função para obter o tipo de veículo mínimo necessário baseado no número de passageiros
  const getMinimumVehicleType = (passengers: number): string => {
    if (passengers <= 3) return 'economico';
    if (passengers === 4) return 'suv';
    if (passengers === 5) return 'mpv';
    if (passengers === 6) return 'minivan';
    if (passengers <= 8) return 'minibus';
    return 'minibus';
  };

  // Atualizar tipo de veículo quando o número de passageiros mudar
  useEffect(() => {
    const numPassengers = parseInt(formData.passengers);
    const currentCapacity = vehicleCapacity[formData.vehicleType];
    
    if (numPassengers > currentCapacity) {
      const newVehicleType = getMinimumVehicleType(numPassengers);
      setFormData(prev => ({ ...prev, vehicleType: newVehicleType }));
    }
  }, [formData.passengers]);

  const routes: PricingRoute[] = [
    { route: 'Aeroporto Faro - Albufeira', economico: 45, conforto: 50, suv: 60, mpv: 65, minivan: 70, minibus: 80, luxo: 135 },
    { route: 'Aeroporto Faro - Vilamoura', economico: 35, conforto: 40, suv: 50, mpv: 55, minivan: 55, minibus: 65, luxo: 90 },
    { route: 'Aeroporto Faro - Quarteira', economico: 35, conforto: 40, suv: 50, mpv: 55, minivan: 55, minibus: 65, luxo: 90 },
    { route: 'Aeroporto Faro - Lagos', economico: 95, conforto: 105, suv: 115, mpv: 125, minivan: 130, minibus: 140, luxo: 190 },
    { route: 'Aeroporto Faro - Portimão', economico: 75, conforto: 85, suv: 95, mpv: 105, minivan: 100, minibus: 115, luxo: 150 },
    { route: 'Aeroporto Faro - Carvoeiro', economico: 70, conforto: 80, suv: 90, mpv: 100, minivan: 95, minibus: 110, luxo: 140 },
    { route: 'Aeroporto Faro - Tavira', economico: 45, conforto: 50, suv: 60, mpv: 65, minivan: 70, minibus: 80, luxo: 135 },
    { route: 'Aeroporto Faro - Sagres', economico: 130, conforto: 145, suv: 155, mpv: 165, minivan: 170, minibus: 190, luxo: 260 },
    { route: 'Aeroporto Faro - Alvor', economico: 75, conforto: 85, suv: 95, mpv: 105, minivan: 100, minibus: 115, luxo: 150 },
    { route: 'Aeroporto Faro - Armação de Pêra', economico: 60, conforto: 65, suv: 75, mpv: 80, minivan: 80, minibus: 95, luxo: 120 },
    { route: 'Aeroporto Faro - Almancil', economico: 30, conforto: 35, suv: 45, mpv: 50, minivan: 45, minibus: 55, luxo: 60 },
    { route: 'Aeroporto Faro - Quinta do Lago', economico: 35, conforto: 40, suv: 50, mpv: 55, minivan: 50, minibus: 60, luxo: 80 },
    { route: 'Aeroporto Faro - Vale do Lobo', economico: 35, conforto: 40, suv: 50, mpv: 55, minivan: 50, minibus: 60, luxo: 80 },
    { route: 'Aeroporto Faro - Olhão', economico: 30, conforto: 35, suv: 45, mpv: 50, minivan: 45, minibus: 55, luxo: 60 },
    { route: 'Aeroporto Faro - Monte Gordo', economico: 70, conforto: 80, suv: 90, mpv: 100, minivan: 90, minibus: 100, luxo: 140 },
    { route: 'Aeroporto Faro - Vila Real de Santo António', economico: 75, conforto: 85, suv: 95, mpv: 105, minivan: 95, minibus: 105, luxo: 150 },
    { route: 'Aeroporto Faro - Ayamonte (Espanha)', economico: 85, conforto: 95, suv: 105, mpv: 115, minivan: 110, minibus: 120, luxo: 170 },
    { route: 'Aeroporto Faro - Huelva (Espanha)', economico: 140, conforto: 160, suv: 170, mpv: 180, minivan: 180, minibus: 200, luxo: 280 },
    { route: 'Aeroporto Faro - Sevilha (Espanha)', economico: 230, conforto: 250, suv: 270, mpv: 280, minivan: 290, minibus: 320, luxo: 450 },
    { route: 'Aeroporto Faro - Lisboa', economico: 290, conforto: 320, suv: 340, mpv: 350, minivan: 360, minibus: 400, luxo: 550 },
    { route: 'Aeroporto Faro - Praia da Rocha', economico: 75, conforto: 85, suv: 95, mpv: 105, minivan: 100, minibus: 115, luxo: 150 },
    { route: 'Aeroporto Faro - Burgau', economico: 100, conforto: 115, suv: 125, mpv: 135, minivan: 135, minibus: 150, luxo: 200 },
    { route: 'Aeroporto Faro - Salema', economico: 110, conforto: 125, suv: 135, mpv: 145, minivan: 145, minibus: 160, luxo: 220 },
    { route: 'Aeroporto Faro - Praia da Luz', economico: 95, conforto: 105, suv: 115, mpv: 125, minivan: 130, minibus: 140, luxo: 190 },
    { route: 'Aeroporto Faro - Montechoro', economico: 45, conforto: 50, suv: 60, mpv: 65, minivan: 70, minibus: 80, luxo: 135 },
    { route: 'Aeroporto Faro - Galé', economico: 50, conforto: 55, suv: 65, mpv: 70, minivan: 75, minibus: 85, luxo: 140 },
    { route: 'Aeroporto Faro - Olhos de Água', economico: 40, conforto: 45, suv: 55, mpv: 60, minivan: 65, minibus: 75, luxo: 120 },
    { route: 'Aeroporto Faro - Ferragudo', economico: 70, conforto: 80, suv: 90, mpv: 100, minivan: 95, minibus: 110, luxo: 140 },
    { route: 'Aeroporto Faro - Porches', economico: 65, conforto: 75, suv: 85, mpv: 95, minivan: 90, minibus: 105, luxo: 130 },
    { route: 'Aeroporto Faro - Silves', economico: 65, conforto: 75, suv: 85, mpv: 95, minivan: 90, minibus: 105, luxo: 130 },
  ];

  // Mapeamento de termos de pesquisa em múltiplos idiomas
  const locationAliases: { [key: string]: string[] } = {
    'Aeroporto Faro': ['aeroporto', 'airport', 'faro airport', 'aeropuerto', 'flughafen', 'aéroport', 'fao'],
    'Albufeira': ['albufeira', 'albufiera'],
    'Vilamoura': ['vilamoura', 'vila moura'],
    'Quarteira': ['quarteira'],
    'Lagos': ['lagos'],
    'Portimão': ['portimao', 'portimão'],
    'Carvoeiro': ['carvoeiro'],
    'Tavira': ['tavira'],
    'Sagres': ['sagres'],
    'Alvor': ['alvor'],
    'Armação de Pêra': ['armacao', 'armação', 'armacao de pera', 'armação de pêra', 'pera'],
    'Almancil': ['almancil'],
    'Quinta do Lago': ['quinta', 'quinta do lago', 'quinta lago'],
    'Vale do Lobo': ['vale', 'vale do lobo', 'vale lobo'],
    'Olhão': ['olhao', 'olhão'],
    'Monte Gordo': ['monte gordo', 'monte'],
    'Vila Real de Santo António': ['vila real', 'vrsa', 'santo antonio', 'santo antónio'],
    'Ayamonte': ['ayamonte', 'espanha', 'spain', 'españa'],
    'Huelva': ['huelva', 'espanha', 'spain', 'españa'],
    'Sevilha': ['sevilha', 'sevilla', 'seville', 'espanha', 'spain', 'españa'],
    'Lisboa': ['lisboa', 'lisbon', 'lisbonne'],
    'Praia da Rocha': ['praia da rocha', 'rocha'],
    'Burgau': ['burgau'],
    'Salema': ['salema'],
    'Praia da Luz': ['praia da luz', 'luz'],
    'Montechoro': ['montechoro'],
    'Galé': ['gale', 'galé'],
    'Olhos de Água': ['olhos', 'olhos de agua', 'olhos de água', 'agua'],
    'Ferragudo': ['ferragudo'],
    'Porches': ['porches'],
    'Silves': ['silves']
  };

  // Função de normalização melhorada
  const normalizeText = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove acentos
      .replace(/[^a-z0-9\s]/g, ''); // Remove caracteres especiais
  };

  // Função para buscar localizações com base no termo de pesquisa
  const searchLocations = (searchTerm: string): string[] => {
    if (!searchTerm || searchTerm.length < 2) return [];

    const normalizedSearch = normalizeText(searchTerm);
    const matches = new Set<string>();

    // Extrair todas as localizações únicas das rotas
    const allLocations = new Set<string>();
    routes.forEach(route => {
      const parts = route.route.split(' - ');
      parts.forEach(part => allLocations.add(part.trim()));
    });

    // Buscar correspondências
    allLocations.forEach(location => {
      const normalizedLocation = normalizeText(location);
      
      // Verificar correspondência direta
      if (normalizedLocation.includes(normalizedSearch)) {
        matches.add(location);
        return;
      }

      // Verificar aliases
      const aliases = locationAliases[location] || [];
      const hasAliasMatch = aliases.some(alias => {
        const normalizedAlias = normalizeText(alias);
        return normalizedAlias.includes(normalizedSearch) || normalizedSearch.includes(normalizedAlias);
      });

      if (hasAliasMatch) {
        matches.add(location);
      }
    });

    return Array.from(matches).slice(0, 5); // Limitar a 5 sugestões
  };

  // Atualizar sugestões quando o usuário digita
  const handleFromChange = (value: string) => {
    setFormData({...formData, from: value});
    const suggestions = searchLocations(value);
    setFromSuggestions(suggestions);
    setShowFromSuggestions(suggestions.length > 0);
  };

  const handleToChange = (value: string) => {
    setFormData({...formData, to: value});
    const suggestions = searchLocations(value);
    setToSuggestions(suggestions);
    setShowToSuggestions(suggestions.length > 0);
  };

  // Selecionar sugestão
  const selectFromSuggestion = (suggestion: string) => {
    setFormData({...formData, from: suggestion});
    setShowFromSuggestions(false);
  };

  const selectToSuggestion = (suggestion: string) => {
    setFormData({...formData, to: suggestion});
    setShowToSuggestions(false);
  };

  // Calcular preço estimado quando origem, destino ou tipo de veículo mudar
  useEffect(() => {
    if (formData.from && formData.to) {
      const fromNormalized = normalizeText(formData.from);
      const toNormalized = normalizeText(formData.to);

      // Procurar a rota em ambas as direções com correspondência inteligente
      const found = routes.find(r => {
        const routeParts = r.route.split(' - ');
        if (routeParts.length !== 2) return false;

        const route1 = routeParts[0].trim();
        const route2 = routeParts[1].trim();
        const route1Normalized = normalizeText(route1);
        const route2Normalized = normalizeText(route2);

        // Verificar aliases para correspondência mais inteligente
        const matchLocation = (input: string, location: string, locationNormalized: string) => {
          if (locationNormalized.includes(input) || input.includes(locationNormalized)) {
            return true;
          }
          const aliases = locationAliases[location] || [];
          return aliases.some(alias => {
            const normalizedAlias = normalizeText(alias);
            return normalizedAlias.includes(input) || input.includes(normalizedAlias);
          });
        };

        // Verificar se a rota corresponde em qualquer direção
        const matchesForward = 
          matchLocation(fromNormalized, route1, route1Normalized) && 
          matchLocation(toNormalized, route2, route2Normalized);
        
        const matchesBackward = 
          matchLocation(toNormalized, route1, route1Normalized) && 
          matchLocation(fromNormalized, route2, route2Normalized);

        return matchesForward || matchesBackward;
      });

      if (found) {
        setMatchedRoute(found);
        const vehicleType = formData.vehicleType as keyof PricingRoute;
        let basePrice = found[vehicleType] as number;
        
        // Aplicar desconto de inverno (10% para Espanha, 30% para Portugal)
        if (winterSeason) {
          basePrice = isSpainRoute(found.route) ? basePrice * 0.9 : basePrice * 0.7;
        }
        
        // Aplicar desconto de ida e volta (10% adicional)
        if (formData.returnTrip) {
          basePrice = basePrice * 2 * 0.9;
        }
        
        setEstimatedPrice(basePrice);
      } else {
        setMatchedRoute(null);
        setEstimatedPrice(null);
      }
    } else {
      setMatchedRoute(null);
      setEstimatedPrice(null);
    }
  }, [formData.from, formData.to, formData.vehicleType, formData.returnTrip, winterSeason]);

  // Calcular preço total incluindo assentos de criança
  const calculateTotalPrice = () => {
    if (estimatedPrice === null) return null;
    
    const childSeatsPrice = 
      (formData.childSeat0to1 * 7) + 
      (formData.childSeat1to3 * 6) + 
      (formData.childSeat5to12 * 4);
    
    return estimatedPrice + childSeatsPrice;
  };

  const totalPrice = calculateTotalPrice();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.acceptPrivacy) {
      alert(t('mustAcceptPrivacy'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Gerar número de reserva
      const reservationNum = Math.floor(Math.random() * 90000) + 10000;
      
      console.log('📝 Criando reserva...');
      
      // Calcular preço total
      const childSeatsPrice = 
        (formData.childSeat0to1 * 7) + 
        (formData.childSeat1to3 * 6) + 
        (formData.childSeat5to12 * 4);
      
      const finalPrice = (estimatedPrice || 0) + childSeatsPrice;
      
      // Preparar dados da reserva
      const reservationData = {
        reservation_number: reservationNum,
        name: formData.name,
        email: formData.email,
        customer_phone: formData.phone || '',
        from_location: formData.from,
        to_location: formData.to,
        travel_date: formData.date,
        travel_time: formData.time,
        passengers: parseInt(formData.passengers),
        vehicle_type: formData.vehicleType,
        return_trip: formData.returnTrip || false,
        return_date: formData.returnDate || null,
        return_time: formData.returnTime || null,
        return_from: formData.differentReturnLocation ? formData.returnFrom : formData.to,
        return_to: formData.differentReturnLocation ? formData.returnTo : formData.from,
        child_seat_0_to_1: formData.childSeat0to1 || 0,
        child_seat_1_to_3: formData.childSeat1to3 || 0,
        child_seat_5_to_12: formData.childSeat5to12 || 0,
        accept_privacy: formData.acceptPrivacy,
        observations: formData.observations || null,
        total_price: finalPrice,
        payment_method: 'none',
        payment_status: 'pending',
        status: 'pending'
      };
      
      console.log('💾 Inserindo na base de dados...');
      
      // Inserir na base de dados
      const { data: reservation, error: dbError } = await supabase
        .from('reservations')
        .insert(reservationData)
        .select()
        .single();

      if (dbError) {
        console.error('❌ Erro ao inserir:', dbError);
        throw new Error(`Erro ao criar reserva: ${dbError.message}`);
      }

      console.log('✅ Reserva criada:', reservationNum);
      
      // Mostrar sucesso
      setSubmitStatus('success');
      setReservationNumber(reservationNum);
      
      // Limpar formulário
      setFormData({
        name: '',
        email: '',
        phone: '',
        from: '',
        to: '',
        date: '',
        time: '',
        passengers: '1',
        vehicleType: 'economico',
        returnTrip: false,
        returnDate: '',
        returnTime: '',
        returnFrom: '',
        returnTo: '',
        differentReturnLocation: false,
        childSeat0to1: 0,
        childSeat1to3: 0,
        childSeat5to12: 0,
        acceptPrivacy: false,
        observations: ''
      });
      setEstimatedPrice(null);
      setMatchedRoute(null);
      
      // Scroll para o topo
      const reservasSection = document.getElementById('reservas');
      if (reservasSection) {
        reservasSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Enviar email em background
      setTimeout(async () => {
        try {
          console.log('📧 Enviando email...');
          
          const emailBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <div style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); padding: 30px; text-align: center;">
                <h1 style="color: white; margin: 0; font-size: 28px;">FARWAYS</h1>
                <p style="color: white; margin: 10px 0 0 0;">Transfers de Qualidade no Algarve</p>
              </div>
              
              <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb;">
                <h2 style="color: #1f2937; margin-top: 0;">✅ Reserva Confirmada!</h2>
                
                <p style="color: #4b5563; font-size: 16px;">Olá <strong>${formData.name}</strong>,</p>
                
                <p style="color: #4b5563;">A sua reserva foi confirmada com sucesso. Aqui estão os detalhes:</p>
                
                <div style="background: #f9fafb; border-left: 4px solid #f97316; padding: 20px; margin: 20px 0;">
                  <h3 style="color: #f97316; margin-top: 0;">🚗 Detalhes da Viagem</h3>
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">📋 Nº Reserva:</td>
                      <td style="padding: 8px 0; color: #1f2937; font-weight: bold;">#${reservationNum}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">📍 De:</td>
                      <td style="padding: 8px 0; color: #1f2937;">${formData.from}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">📍 Para:</td>
                      <td style="padding: 8px 0; color: #1f2937;">${formData.to}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">📅 Data:</td>
                      <td style="padding: 8px 0; color: #1f2937;">${new Date(formData.date).toLocaleDateString('pt-PT')}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">🕐 Hora:</td>
                      <td style="padding: 8px 0; color: #1f2937;">${formData.time}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">👥 Passageiros:</td>
                      <td style="padding: 8px 0; color: #1f2937;">${formData.passengers}</td>
                    </tr>
                    <tr>
                      <td style="padding: 8px 0; color: #6b7280; font-weight: bold;">💰 Valor:</td>
                      <td style="padding: 8px 0; color: #16a34a; font-size: 20px; font-weight: bold;">€${finalPrice.toFixed(2)}</td>
                    </tr>
                  </table>
                </div>
                
                <p style="color: #4b5563;">Obrigado por escolher a FARWAYS!</p>
              </div>
              
              <div style="background: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
                <p style="color: #6b7280; margin: 0; font-size: 14px;">
                  📧 reservas@farways.pt | 📱 +351 937 545 000
                </p>
              </div>
            </div>
          `;

          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-email-smtp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              to: formData.email,
              subject: `Confirmação de Reserva FARWAYS #${reservationNum}`,
              html: emailBody,
              from: 'FARWAYS <reservas@farways.pt>'
            }),
          });

          console.log('✅ Email enviado');
        } catch (emailError) {
          console.warn('⚠️ Erro ao enviar email:', emailError);
        }
      }, 500);

    } catch (error) {
      console.error('❌ Erro:', error);
      alert('Erro ao criar reserva. Por favor, tente novamente.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservas" className="py-20 bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <i className="ri-map-pin-user-line text-3xl text-orange-500"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            {t('bookingTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('bookingSubtitle')}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100 relative">
          {submitStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-200 text-green-800 px-6 py-4 rounded-lg mb-6">
              <div className="flex items-center justify-center space-x-3">
                <i className="ri-checkbox-circle-line text-3xl"></i>
                <div className="text-center">
                  <strong className="text-xl">{t('bookingConfirmed')}</strong>
                  {reservationNumber && (
                    <div className="mt-2 text-lg">
                      {t('reservationNumber')}: <strong className="text-2xl">#{reservationNumber}</strong>
                    </div>
                  )}
                  <div className="text-sm mt-1">
                    {t('pdfSentTo')} {formData.email || 'o seu email'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 text-red-800 px-6 py-4 rounded-lg mb-6 text-center">
              <i className="ri-error-warning-line text-2xl"></i>
              <div className="mt-2">{t('bookingError')}</div>
            </div>
          )}

          <form id="reservas-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados do Cliente */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('name')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder={t('fullName')}
                    required
                  />
                  <i className="ri-user-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="seu@email.com"
                    required
                  />
                  <i className="ri-mail-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('phone')}
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="+351 XXX XXX XXX"
                  />
                  <i className="ri-phone-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>
            </div>

            {/* Detalhes da Viagem */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('from')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="from"
                    value={formData.from}
                    onChange={(e) => handleFromChange(e.target.value)}
                    onFocus={() => formData.from && setShowFromSuggestions(fromSuggestions.length > 0)}
                    onBlur={() => setTimeout(() => setShowFromSuggestions(false), 200)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Aeroporto de Faro"
                    required
                    autoComplete="off"
                  />
                  <i className="ri-map-pin-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  {showFromSuggestions && fromSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {fromSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => selectFromSuggestion(suggestion)}
                          className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm text-gray-700 flex items-center"
                        >
                          <i className="ri-map-pin-line text-orange-500 mr-2"></i>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('to')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="to"
                    value={formData.to}
                    onChange={(e) => handleToChange(e.target.value)}
                    onFocus={() => formData.to && setShowToSuggestions(toSuggestions.length > 0)}
                    onBlur={() => setTimeout(() => setShowToSuggestions(false), 200)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder={t('destination')}
                    required
                    autoComplete="off"
                  />
                  <i className="ri-map-pin-2-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  {showToSuggestions && toSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {toSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => selectToSuggestion(suggestion)}
                          className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm text-gray-700 flex items-center"
                        >
                          <i className="ri-map-pin-2-line text-orange-500 mr-2"></i>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('date')}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                  <i className="ri-calendar-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('time')}
                </label>
                <div className="relative">
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                  <i className="ri-time-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('passengers')}
                </label>
                <div className="relative">
                  <select
                    name="passengers"
                    value={formData.passengers}
                    onChange={(e) => setFormData({...formData, passengers: e.target.value})}
                    className="w-full px-4 py-3 pl-12 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none"
                  >
                    {[1,2,3,4,5,6,7,8].map(num => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? t('passengerSingular') : t('passengerPlural')}
                      </option>
                    ))}
                  </select>
                  <i className="ri-user-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('vehicleType')}
                </label>
                <div className="relative">
                  <select
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
                    className="w-full px-4 py-3 pl-12 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none"
                  >
                    <option value="economico" disabled={parseInt(formData.passengers) > 3}>
                      {t('economico')} (1-3 pax)
                    </option>
                    <option value="conforto" disabled={parseInt(formData.passengers) > 3}>
                      {t('conforto')} (1-3 pax)
                    </option>
                    <option value="suv" disabled={parseInt(formData.passengers) > 4}>
                      {t('suv')} (1-4 pax)
                    </option>
                    <option value="mpv" disabled={parseInt(formData.passengers) > 5}>
                      {t('mpv')} (1-5 pax)
                    </option>
                    <option value="minivan" disabled={parseInt(formData.passengers) > 6}>
                      {t('minivan')} (1-6 pax)
                    </option>
                    <option value="minibus">
                      {t('minibus')} (1-8 pax)
                    </option>
                    <option value="luxo" disabled={parseInt(formData.passengers) > 3}>
                      {t('luxo')} (1-3 pax)
                    </option>
                  </select>
                  <i className="ri-car-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
                {parseInt(formData.passengers) > vehicleCapacity[formData.vehicleType] && (
                  <div className="mt-2 text-xs text-orange-600 flex items-start">
                    <i className="ri-information-line mr-1 mt-0.5"></i>
                    <span>Veículo ajustado automaticamente para {formData.passengers} passageiros</span>
                  </div>
                )}
              </div>

              <div className="flex items-center md:col-span-2">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="returnTrip"
                    checked={formData.returnTrip}
                    onChange={(e) => setFormData({...formData, returnTrip: e.target.checked})}
                    className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('returnTrip')} (-10%)
                  </span>
                </label>
              </div>
            </div>

            {/* Detalhes da Viagem de Regresso */}
            {formData.returnTrip && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-arrow-left-right-line mr-2 text-orange-500"></i>
                  Detalhes da Viagem de Regresso
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Regresso
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                        min={formData.date}
                        className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        required={formData.returnTrip}
                      />
                      <i className="ri-calendar-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de Regresso
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        name="returnTime"
                        value={formData.returnTime}
                        onChange={(e) => setFormData({...formData, returnTime: e.target.value})}
                        className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        required={formData.returnTrip}
                      />
                      <i className="ri-time-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.differentReturnLocation}
                        onChange={(e) => setFormData({...formData, differentReturnLocation: e.target.checked})}
                        className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        <i className="ri-map-pin-2-line mr-1"></i>
                        Local de pick-up diferente no regresso
                      </span>
                    </label>
                  </div>

                  {formData.differentReturnLocation && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pick-up no Regresso (De)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="returnFrom"
                            value={formData.returnFrom}
                            onChange={(e) => setFormData({...formData, returnFrom: e.target.value})}
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                            placeholder="Local de pick-up no regresso"
                            required={formData.differentReturnLocation}
                            autoComplete="off"
                          />
                          <i className="ri-map-pin-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Destino no Regresso (Para)
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="returnTo"
                            value={formData.returnTo}
                            onChange={(e) => setFormData({...formData, returnTo: e.target.value})}
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                            placeholder="Destino no regresso"
                            required={formData.differentReturnLocation}
                            autoComplete="off"
                          />
                          <i className="ri-map-pin-2-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                      </div>
                    </>
                  )}

                  {!formData.differentReturnLocation && (
                    <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                        <div className="text-sm text-blue-800">
                          <strong>Rota de Regresso:</strong>
                          <p className="mt-1">
                            {formData.to || '[Destino]'} → {formData.from || '[Origem]'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assentos para Crianças */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <i className="ri-parent-line mr-2 text-orange-500"></i>
                {t('childSeats')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('childSeat0to1')}
                  </label>
                  <div className="relative">
                    <select
                      name="childSeat0to1"
                      value={formData.childSeat0to1}
                      onChange={(e) => setFormData({...formData, childSeat0to1: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 pl-12 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none"
                    >
                      {[0,1,2,3,4].map(num => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? t('seat') : t('seats')}
                        </option>
                      ))}
                    </select>
                    <i className="ri-baby-carriage-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('childSeat1to3')}
                  </label>
                  <div className="relative">
                    <select
                      name="childSeat1to3"
                      value={formData.childSeat1to3}
                      onChange={(e) => setFormData({...formData, childSeat1to3: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 pl-12 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none"
                    >
                      {[0,1,2,3,4].map(num => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? t('seat') : t('seats')}
                        </option>
                      ))}
                    </select>
                    <i className="ri-baby-carriage-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('childSeat5to12')}
                  </label>
                  <div className="relative">
                    <select
                      name="childSeat5to12"
                      value={formData.childSeat5to12}
                      onChange={(e) => setFormData({...formData, childSeat5to12: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 pl-12 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm appearance-none"
                    >
                      {[0,1,2,3,4].map(num => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? t('seat') : t('seats')}
                        </option>
                      ))}
                    </select>
                    <i className="ri-baby-carriage-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Observações */}
            <div className="pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-message-3-line mr-2 text-orange-500"></i>
                {t('observations')}
              </label>
              <textarea
                name="observations"
                value={formData.observations}
                onChange={(e) => setFormData({...formData, observations: e.target.value})}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                placeholder={t('observationsPlaceholder')}
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.observations.length}/500</p>
            </div>

            {/* Preço Estimado */}
            {estimatedPrice !== null && matchedRoute && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-green-800 font-medium">
                    <i className="ri-price-tag-3-line mr-2"></i>
                    {t('totalValue')}:
                  </div>
                  <div className="text-4xl font-bold text-green-600">
                    €{totalPrice?.toFixed(2)}
                  </div>
                </div>
                <div className="text-xs text-green-700 space-y-1">
                  <div>
                    <i className="ri-map-pin-line mr-1"></i>
                    Rota: {formData.from} → {formData.to}
                  </div>
                  {winterSeason && (
                    <div>
                      <i className="ri-snowflake-line mr-1"></i>
                      {t('winterDiscountApplied')}
                    </div>
                  )}
                  {formData.returnTrip && (
                    <div>
                      <i className="ri-arrow-left-right-line mr-1"></i>
                      {t('discountApplied')}
                    </div>
                  )}
                  {(formData.childSeat0to1 > 0 || formData.childSeat1to3 > 0 || formData.childSeat5to12 > 0) && (
                    <div className="pt-2 border-t border-green-300 mt-2">
                      <div className="font-medium mb-1">{t('childSeatsIncluded')}:</div>
                      {formData.childSeat0to1 > 0 && (
                        <div>• {formData.childSeat0to1}x {t('childSeat0to1Short')} (€7 cada) = €{formData.childSeat0to1 * 7}</div>
                      )}
                      {formData.childSeat1to3 > 0 && (
                        <div>• {formData.childSeat1to3}x {t('childSeat1to3Short')} (€6 cada) = €{formData.childSeat1to3 * 6}</div>
                      )}
                      {formData.childSeat5to12 > 0 && (
                        <div>• {formData.childSeat5to12}x {t('childSeat5to12Short')} (€4 cada) = €{formData.childSeat5to12 * 4}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mensagem quando rota não encontrada */}
            {formData.from && formData.to && !matchedRoute && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-yellow-600 text-xl mt-0.5"></i>
                  <div className="text-sm">
                    <strong>{t('routeNotInTable')}</strong>
                    <p className="mt-1">{t('contactForQuote')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Política de Privacidade */}
            <div className="pt-4 border-t border-gray-200">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onChange={(e) => setFormData({...formData, acceptPrivacy: e.target.checked})}
                  className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500 mt-0.5"
                  required
                />
                <span className="text-sm text-gray-700">
                  {t('acceptPrivacyPart1')}{' '}
                  <Link 
                    to={i18n.language === 'es' ? '/politica-de-privacidad' : i18n.language === 'en' ? '/privacy-policy' : '/politica-de-privacidade'}
                    className="text-orange-500 hover:text-orange-600 underline"
                    target="_blank"
                  >
                    {t('privacyPolicy')}
                  </Link>
                  {t('acceptPrivacyPart2')}
                </span>
              </label>
            </div>

            <div className="text-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting || !formData.acceptPrivacy}
                className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>A processar...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    <span>Confirmar Reserva</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
